import { Inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { DashboardSnapshot } from '@ddv/models';
import { combineLatest, map, Observable, of, ReplaySubject, switchMap, take } from 'rxjs';

import { DashboardGroup, DashboardGroupFromAwsApi } from '../models/dashboard-group';

@Injectable()
export class DashboardGroupsService {
    public readonly dashboardGroups$: Observable<DashboardGroup[]>;

    private readonly dashboardGroups: ReplaySubject<DashboardGroup[]> = new ReplaySubject<DashboardGroup[]>(1);
    private clientCode = '';

    constructor(
        private readonly currentStateService: CurrentStateService,
        @Inject(ApiServices.ddvMW) private readonly legacyMW: ApiExecutorService,
        @Inject(ApiServices.ddvApi) private readonly api: ApiExecutorService,
        private readonly featureFlags: FeatureFlagService,
    ) {
        this.dashboardGroups$ = this.dashboardGroups.asObservable();

        this.currentStateService.clientCode$
            .subscribe({
                next: (clientCode) => {
                    this.clientCode = clientCode;
                    this.getDashboardGroups()
                        .subscribe({
                            next: (groups) => this.dashboardGroups.next(groups),
                        });
                },
            });
    }

    getDashboardGroups(clientCode?: string): Observable<DashboardGroup[]> {
        return this.featureFlags.isFlagEnabled('use-aws-for-dashboard-groups', clientCode ?? this.clientCode)
            .pipe(
                switchMap((useAws) => {
                    if (!useAws) {
                        return this.legacyMW.invokeServiceWithParams<Partial<DashboardGroup>[]>(clientCode ?? this.clientCode, 'dashboard-groups');
                    }

                    return this.api.invokeService<DashboardGroupFromAwsApi[]>(clientCode ?? this.clientCode, 'dashboard-groups', 'GET');
                }),
                map((groupsJSON) => groupsJSON.map((groupJSON) => new DashboardGroup(groupJSON))),
            );
    }

    addDashboardGroup(group: DashboardGroup): Observable<DashboardGroup> {
        return this.featureFlags.isFlagEnabled('use-aws-for-dashboard-groups', this.clientCode)
            .pipe(
                take(1),
                switchMap((useAws) => {
                    if (!useAws) {
                        return this.legacyMW.invokeServiceWithBody<DashboardGroup>(this.clientCode, 'dashboard-groups', 'POST', group);
                    }

                    const groupForApi = {
                        name: group.name,
                        dashboards: group.dashboards.map((d) => d.id),
                    };
                    return this.api.invokeServiceWithBody<DashboardGroupFromAwsApi>(this.clientCode, 'dashboard-groups', 'POST', groupForApi);
                }),
                map((g) => {
                    if (!('isDefault' in g)) {
                        return {
                            ...g,
                            isDefault: true,
                            dashboards: g.dashboards.map((d) => ({ id: d })),
                            // deprecated - delete when we no longer support groups from mw
                            sortOrder: -1,
                        } as DashboardGroup;
                    }
                    return g as DashboardGroup;
                }),
            );
    }

    updateDashboardGroup(group: DashboardGroup): Observable<DashboardGroup> {
        return this.featureFlags.isFlagEnabled('use-aws-for-dashboard-groups', this.clientCode)
            .pipe(
                take(1),
                switchMap((useAws) => {
                    if (!useAws) {
                        return combineLatest([
                            this.legacyMW.invokeServiceWithBody<Partial<DashboardGroup>>(
                                this.clientCode,
                                `dashboard-groups/${group.id}`,
                                'PUT',
                                group,
                            ),
                            of(group),
                            of(useAws),
                        ]);
                    }

                    const groupForApi = {
                        dashboards: group.dashboards.map((d) => d.id),
                    };
                    return combineLatest([
                        this.api.invokeServiceWithBody<DashboardGroupFromAwsApi>(
                            this.clientCode,
                            `dashboard-groups/${group.id}`,
                            'PATCH',
                            groupForApi,
                        ),
                        of(group),
                        of(useAws),
                    ]);
                }),
                map(([g, originalGroup, useAws]) => {
                    if (!useAws) {
                        return g as DashboardGroup;
                    }
                    return {
                        ...g,
                        isDefault: originalGroup.isDefault,
                        dashboards: (g.dashboards ?? []).map((d) => ({ id: d } as unknown as DashboardSnapshot)),
                        // deprecated - delete when we no longer support groups from mw
                        sortOrder: -1,
                    } as DashboardGroup;
                }),
            );
    }

    deleteDashboardGroup(id: string | number): Observable<void> {
        return this.featureFlags.isFlagEnabled('use-aws-for-dashboard-groups', this.clientCode)
            .pipe(
                take(1),
                switchMap((useAws) => {
                    if (!useAws) {
                        return this.legacyMW.invokeService<void>(this.clientCode, `dashboard-groups/${id}`, 'DELETE');
                    }

                    return this.api.invokeService<void>(this.clientCode, `dashboard-groups/${id}`, 'DELETE');
                }),
            );
    }

    toggleDefault(clientCode: string, id: string | number, isDefault: boolean): Observable<boolean> {
        const method = isDefault ? 'PUT' : 'DELETE';
        return this.api.invokeService(clientCode, `user/state/open-dashboard-groups/${id}`, method)
            .pipe(map(() => isDefault));
    }

    refetchDashboardGroups(): void {
        this.getDashboardGroups()
            .subscribe({
                next: (groups) => this.dashboardGroups.next(groups),
            });
    }
}
